import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  HandHeart, CreditCard, Landmark, Users, FileText, Smartphone,
  Check, ShieldCheck, ArrowUpRight, Download, BadgeCheck, Building2, Mail,
} from "lucide-react";

const GOLD = "#D4AF37";
const INDIGO = "#4338CA";
const FOREST = "#059669";

const COMMUNITY_PHOTO = "https://images.unsplash.com/photo-1599059813005-11265ba4b4ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwdm9sdW50ZWVycyUyMGNvbW11bml0eSUyMGZvb2QlMjBkcml2ZXxlbnwwfHx8fDE3OTE5MDI5NDZ8MA&ixlib=rb-4.1.0&q=85";
const CIVIC_TEXTURE = "https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmFsJTIwYnVpbGRpbmclMjBmYWNhZGUlMjBkYXJrJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODg4NTg4NjV8MA&ixlib=rb-4.1.0&q=85";

const DONORS = [
  { initials: "MK", name: "Margaret K.", amount: "$100", when: "2 min ago" },
  { initials: "JR", name: "James R.", amount: "$50", when: "14 min ago" },
  { initials: "AS", name: "Aisha S.", amount: "$250", when: "1 hr ago" },
  { initials: "DT", name: "Daniel T.", amount: "$25", when: "3 hr ago" },
];

const PILLARS = [
  {
    id: "gateway",
    icon: CreditCard,
    title: "Direct Contribution Gateway",
    desc: "Donors give in two taps. Funds settle straight to your organization's account — no middleman skimming the mission.",
  },
  {
    id: "showcase",
    icon: Users,
    title: "Community Program Showcase",
    desc: "Every initiative gets a clean program page with live volunteer sign-up CTAs, so outreach converts on the spot.",
  },
  {
    id: "vault",
    icon: FileText,
    title: "Governance & 990 Vault",
    desc: "Board minutes, annual reports and Form 990s published behind one-click PDF downloads. Radical transparency, zero maintenance.",
  },
  {
    id: "mobile",
    icon: Smartphone,
    title: "Mobile-First Outreach",
    desc: "Gala QR scans land on a sub-second mobile donation flow. Built for the ballroom, not just the boardroom.",
  },
];

const OFFER_ITEMS = [
  "Full 4-page responsive build",
  "Donation gateway setup (No third-party SaaS cut)",
  "Governance & transparency PDF vault",
  "7-day turnaround from asset handover",
];

export default function ImpactLanding() {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [amount, setAmount] = useState(50);
  const [meterOn, setMeterOn] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/settings/feature-flags")
      .then((r) => { if (alive) setAllowed(r.data?.impact_page_enabled !== false); })
      .catch(() => { if (alive) setAllowed(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (allowed === false) navigate("/", { replace: true });
  }, [allowed, navigate]);

  useEffect(() => {
    if (allowed) {
      const t = setTimeout(() => setMeterOn(true), 400);
      return () => clearTimeout(t);
    }
  }, [allowed]);

  if (allowed !== true) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center" data-testid="impact-loading">
        <div className="font-jakarta text-xs uppercase tracking-[0.35em] text-[#D4AF37] animate-pulse">Loading Initiative…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14] text-[#EDEAE2] font-jakarta" data-testid="impact-page">
      {/* ============ HEADER ============ */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0E14]/85 border-b border-[#D4AF37]/15">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="impact-header-brand">
            <span className="font-extrabold tracking-tight text-lg">Trace<span className="text-[#D4AF37]">Elements</span></span>
            <span className="hidden sm:inline-block h-4 w-px bg-[#D4AF37]/30" />
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.3em] text-[#EDEAE2]/60">Impact &amp; Endowment Engine</span>
          </Link>
          <Link
            to="/terminal"
            data-testid="impact-header-cta"
            className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#D4AF37] hover:text-[#0B0E14] transition-colors duration-300"
          >
            Reserve a Build Slot <ArrowUpRight size={13} />
          </Link>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#D4AF37]/10" data-testid="impact-hero">
        <div
          className="absolute inset-0 opacity-[0.07] bg-cover bg-center pointer-events-none"
          style={{ backgroundImage: `url(${CIVIC_TEXTURE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14]/40 via-transparent to-[#0B0E14] pointer-events-none" />
        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <div
              className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-4 py-2 text-[10px] uppercase tracking-[0.28em] font-semibold"
              data-testid="impact-hero-badge"
            >
              <ShieldCheck size={13} /> 501(c)(3) &amp; Community Development Initiative
            </div>
            <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="impact-hero-headline">
              The Impact &amp;<br />Endowment <span className="text-[#D4AF37]">Engine.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#EDEAE2]/70 max-w-xl" data-testid="impact-hero-subcopy">
              Institutional-grade digital infrastructure for non-profits, foundations, and community development organizations.
              Permanent. Transparent. Built to outlast the campaign season.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/terminal"
                data-testid="impact-hero-cta-offer"
                className="inline-flex items-center gap-2 bg-[#4338CA] text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#5246e0] hover:shadow-[0_0_28px_rgba(67,56,202,0.5)] transition-all duration-300"
              >
                Claim the Cohort Offer <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/the-engine"
                data-testid="impact-hero-cta-architecture"
                className="inline-flex items-center gap-2 border border-[#EDEAE2]/25 text-[#EDEAE2]/80 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors duration-300"
              >
                Inspect the Architecture
              </Link>
            </div>
            <div className="mt-8 text-[11px] uppercase tracking-[0.25em] text-[#EDEAE2]/40">
              Deployed by TraceElements · Civic Web Division
            </div>
          </div>

          {/* Dashboard mock */}
          <div className="lg:col-span-6">
            <div className="border border-[#D4AF37]/25 bg-[#11151D] shadow-[0_30px_80px_rgba(0,0,0,0.5)]" data-testid="impact-dashboard-mock">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#EDEAE2]/10">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#EDEAE2]/50">Organization Dashboard</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#34d399]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#34d399] animate-pulse" /> Live Mock
                </span>
              </div>

              {/* Donation slider */}
              <div className="px-5 py-5 border-b border-[#EDEAE2]/10">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#EDEAE2]/50">Choose Contribution</span>
                  <span className="text-2xl font-extrabold text-[#D4AF37]" data-testid="donation-amount-readout">${amount}</span>
                </div>
                <div className="flex gap-2 mb-4">
                  {[25, 50, 100].map((v) => (
                    <button
                      key={v}
                      onClick={() => setAmount(v)}
                      data-testid={`donation-chip-${v}`}
                      className={`flex-1 py-2.5 text-sm font-bold border transition-colors duration-200 ${
                        amount === v
                          ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                          : "border-[#EDEAE2]/15 text-[#EDEAE2]/60 hover:border-[#D4AF37]/50 hover:text-[#EDEAE2]"
                      }`}
                    >
                      ${v}
                    </button>
                  ))}
                  <button
                    onClick={() => setAmount(175)}
                    data-testid="donation-chip-custom"
                    className={`flex-1 py-2.5 text-sm font-bold border transition-colors duration-200 ${
                      ![25, 50, 100].includes(amount)
                        ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                        : "border-[#EDEAE2]/15 text-[#EDEAE2]/60 hover:border-[#D4AF37]/50 hover:text-[#EDEAE2]"
                    }`}
                  >
                    Custom
                  </button>
                </div>
                <div className="relative h-1.5 bg-[#EDEAE2]/10">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#4338CA] to-[#D4AF37] transition-all duration-500"
                    style={{ width: `${Math.min((amount / 200) * 100, 100)}%` }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full bg-[#D4AF37] border-2 border-[#0B0E14] transition-all duration-500"
                    style={{ left: `calc(${Math.min((amount / 200) * 100, 100)}% - 7px)` }}
                  />
                </div>
              </div>

              {/* Donor wall */}
              <div className="px-5 py-4 border-b border-[#EDEAE2]/10">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EDEAE2]/50 mb-3">Donor Wall · Recent Gifts</div>
                <div className="space-y-2.5">
                  {DONORS.map((d) => (
                    <div key={d.initials} className="flex items-center gap-3">
                      <span className="h-7 w-7 rounded-full bg-[#4338CA]/30 border border-[#4338CA]/60 flex items-center justify-center text-[10px] font-bold text-[#c7c2f5]">
                        {d.initials}
                      </span>
                      <span className="text-sm text-[#EDEAE2]/80 flex-1">{d.name}</span>
                      <span className="text-sm font-bold text-[#D4AF37]">{d.amount}</span>
                      <span className="text-[10px] text-[#EDEAE2]/35 w-16 text-right">{d.when}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Endowment progress */}
              <div className="px-5 py-5">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#EDEAE2]/50">Endowment Progress</span>
                  <span className="text-xl font-extrabold text-[#D4AF37]" data-testid="endowment-percent">78%</span>
                </div>
                <div className="h-2.5 border border-[#D4AF37]/30 bg-[#0B0E14] p-[2px]" data-testid="endowment-meter">
                  <div
                    className="h-full bg-gradient-to-r from-[#059669] to-[#34d399] transition-all duration-[1400ms] ease-out"
                    style={{ width: meterOn ? "78%" : "0%" }}
                  />
                </div>
                <div className="mt-2 text-[10px] text-[#EDEAE2]/35 uppercase tracking-[0.2em]">$780,000 of $1,000,000 community endowment goal</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ METRICS RIBBON ============ */}
      <section className="bg-[#131926] border-b border-[#D4AF37]/15" data-testid="impact-metrics-ribbon">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-6">
          {[
            { stat: "100%", label: "Transparent Donor Tracking" },
            { stat: "0%", label: "Unnecessary Platform Bloat" },
            { stat: "<1s", label: "Mobile Donor Load Speed" },
          ].map((m) => (
            <div key={m.label} className="text-center sm:text-left" data-testid={`metric-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-4xl lg:text-5xl font-extrabold text-[#D4AF37] tracking-tight">{m.stat}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.28em] text-[#EDEAE2]/55">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FOUR TRUST MODULES ============ */}
      <section className="py-20 lg:py-28 border-b border-[#D4AF37]/10" data-testid="impact-pillars">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4">// Core Pillars</div>
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight max-w-2xl" data-testid="impact-pillars-heading">
            The Four Trust Modules.
          </h2>
          <p className="mt-4 text-[#EDEAE2]/60 max-w-xl">
            Every deployment ships with the same institutional foundation — engineered once, proven across the cohort.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {/* Card 1 — Contribution Gateway */}
            <PillarCard pillar={PILLARS[0]} testid="pillar-gateway">
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 bg-[#0B0E14] border border-[#EDEAE2]/15 py-2.5 text-xs font-semibold text-[#EDEAE2]/85">
                  <CreditCard size={13} /> Apple Pay
                </div>
                <div className="flex items-center justify-center gap-2 bg-[#4338CA] py-2.5 text-xs font-bold text-white">
                  <Landmark size={13} /> Stripe
                </div>
                <div className="flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] py-2.5 text-xs font-semibold">
                  <Building2 size={13} /> Bank ACH
                </div>
              </div>
            </PillarCard>

            {/* Card 2 — Program Showcase */}
            <PillarCard pillar={PILLARS[1]} testid="pillar-showcase">
              <div className="relative overflow-hidden border border-[#EDEAE2]/10">
                <img src={COMMUNITY_PHOTO} alt="Community volunteers at a food drive" className="h-24 w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/90 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-[#EDEAE2]">Riverbend Food Drive</span>
                  <span className="text-[9px] uppercase tracking-[0.15em] bg-[#D4AF37] text-[#0B0E14] px-2 py-1 font-bold">Volunteer Sign-Up</span>
                </div>
              </div>
            </PillarCard>

            {/* Card 3 — 990 Vault */}
            <PillarCard pillar={PILLARS[2]} testid="pillar-vault">
              <div className="flex items-center gap-3 border border-[#EDEAE2]/10 bg-[#0B0E14] p-3">
                <div className="flex-1 space-y-1.5">
                  <div className="h-1.5 w-4/5 bg-[#EDEAE2]/15" />
                  <div className="h-1.5 w-3/5 bg-[#EDEAE2]/15" />
                  <div className="h-1.5 w-2/3 bg-[#D4AF37]/40" />
                  <div className="pt-1 text-[10px] font-semibold text-[#EDEAE2]/70">Form 990 — FY2025.pdf</div>
                </div>
                <span className="h-10 w-10 shrink-0 rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37]">
                  <Download size={15} />
                </span>
              </div>
            </PillarCard>

            {/* Card 4 — Mobile Outreach */}
            <PillarCard pillar={PILLARS[3]} testid="pillar-mobile">
              <div className="flex items-center gap-3">
                <div className="w-16 shrink-0 border border-[#EDEAE2]/20 bg-[#0B0E14] rounded-xl p-1.5">
                  <div className="rounded-lg bg-[#131926] p-1.5 space-y-1">
                    <div className="h-1 w-3/4 mx-auto bg-[#D4AF37]/60 rounded-full" />
                    <div className="h-6 rounded bg-[#4338CA]/40 border border-[#4338CA]/70" />
                    <div className="h-1 w-1/2 mx-auto bg-[#EDEAE2]/20 rounded-full" />
                    <div className="h-3 rounded bg-[#059669]/50 border border-[#059669]/70" />
                  </div>
                </div>
                <div className="flex-1">
                  <QrMock />
                  <div className="mt-1.5 text-[9px] uppercase tracking-[0.2em] text-[#EDEAE2]/45 text-center">Gala QR Scan → Donate</div>
                </div>
              </div>
            </PillarCard>
          </div>
        </div>
      </section>

      {/* ============ COHORT OFFER ============ */}
      <section className="py-20 lg:py-28" data-testid="impact-offer">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="relative bg-[#12161D] border border-[#D4AF37]/40 p-8 lg:p-14" data-testid="impact-offer-card">
            <span className="absolute top-0 left-0 h-10 w-10 border-t-2 border-l-2 border-[#D4AF37]" />
            <span className="absolute bottom-0 right-0 h-10 w-10 border-b-2 border-r-2 border-[#D4AF37]" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4">// The Non-Profit Cohort Offer</div>
                <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="impact-offer-heading">
                  Community Deployment,<br />Flat Rate.
                </h2>
                <div className="mt-8 flex items-end gap-3">
                  <span className="text-6xl lg:text-7xl font-extrabold text-[#D4AF37] tracking-tighter" data-testid="impact-offer-price">$450</span>
                  <span className="pb-2 text-[11px] uppercase tracking-[0.22em] text-[#EDEAE2]/55 leading-relaxed">
                    Community Deployment<br />Flat Rate
                  </span>
                </div>
                <p className="mt-6 text-sm text-[#EDEAE2]/55 leading-relaxed max-w-md">
                  A grant-style engagement for qualifying 501(c)(3) and community development organizations.
                  No third-party SaaS cut. No recurring platform tax. Your endowment stays yours.
                </p>
              </div>
              <div>
                <ul className="space-y-4">
                  {OFFER_ITEMS.map((item, i) => (
                    <li key={item} className="flex items-start gap-3" data-testid={`offer-check-${i}`}>
                      <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/60 flex items-center justify-center">
                        <Check size={12} className="text-[#D4AF37]" strokeWidth={3} />
                      </span>
                      <span className="text-sm text-[#EDEAE2]/85">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/terminal"
                  data-testid="impact-offer-cta"
                  className="mt-10 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B0E14] px-8 py-4 text-xs uppercase tracking-[0.22em] font-extrabold hover:bg-[#e5c563] hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all duration-300"
                >
                  Begin Asset Handover <ArrowUpRight size={15} />
                </Link>
                <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#EDEAE2]/35">
                  Routed through the TraceElements project terminal · 24h response
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ VERIFICATION FOOTER ============ */}
      <footer className="border-t border-[#D4AF37]/15 bg-[#0D1017]" data-testid="impact-verification-bar">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#EDEAE2]/45 mb-4">Civic Trust Signals</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 border border-[#059669]/50 text-[#34d399] px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold" data-testid="badge-eoc">
                <BadgeCheck size={12} /> EOC Registered Vendor
              </span>
              <span className="inline-flex items-center gap-1.5 border border-[#059669]/50 text-[#34d399] px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold" data-testid="badge-losb">
                <BadgeCheck size={12} /> LOSB Registered Vendor
              </span>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#EDEAE2]/45 mb-4">Contact Routing</div>
            <Link to="/terminal" className="inline-flex items-center gap-2 text-sm text-[#EDEAE2]/75 hover:text-[#D4AF37] transition-colors" data-testid="impact-footer-contact">
              <Mail size={14} className="text-[#D4AF37]" /> Terminal intake → 24h response window
            </Link>
            <div className="mt-3 text-xs text-[#EDEAE2]/40 leading-relaxed">
              Every enquiry is logged, timestamped, and answered by a principal engineer — never a sales queue.
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#EDEAE2]/45 mb-4">Developer Architecture</div>
            <Link to="/the-engine" className="inline-flex items-center gap-2 text-sm text-[#EDEAE2]/75 hover:text-[#D4AF37] transition-colors" data-testid="impact-footer-architecture">
              <HandHeart size={14} className="text-[#D4AF37]" /> Inspect the TraceElements Engine
            </Link>
            <div className="mt-3 text-xs text-[#EDEAE2]/40 leading-relaxed">
              The same architecture that powers our commercial fleet, donated at cohort rate.
            </div>
          </div>
        </div>
        <div className="border-t border-[#EDEAE2]/10">
          <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#EDEAE2]/35">© 2026 TraceElements · Impact &amp; Endowment Engine</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#EDEAE2]/35">A civic deployment initiative</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PillarCard({ pillar, children, testid }) {
  const Icon = pillar.icon;
  return (
    <div
      className="group bg-[#11151D] border border-[#EDEAE2]/10 hover:border-[#D4AF37]/50 p-6 flex flex-col transition-colors duration-300"
      data-testid={testid}
    >
      <span className="h-11 w-11 border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] mb-5">
        <Icon size={18} />
      </span>
      <h3 className="text-lg font-bold tracking-tight">{pillar.title}</h3>
      <p className="mt-2.5 text-[13px] leading-relaxed text-[#EDEAE2]/55 flex-1">{pillar.desc}</p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function QrMock() {
  const cells = [
    1,1,1,0,1,0,1,1,1,
    1,0,1,0,0,1,1,0,1,
    1,1,1,0,1,1,1,1,1,
    0,0,0,1,0,0,0,0,0,
    1,0,1,1,1,0,1,0,1,
    0,1,0,0,1,1,0,1,0,
    1,1,1,0,1,0,1,1,1,
    1,0,1,1,0,1,1,0,1,
    1,1,1,0,1,1,0,1,1,
  ];
  return (
    <div className="grid grid-cols-9 gap-[1.5px] w-fit mx-auto bg-[#0B0E14] p-1.5 border border-[#EDEAE2]/15" data-testid="qr-mock">
      {cells.map((c, i) => (
        <span key={i} className={`h-[5px] w-[5px] ${c ? "bg-[#EDEAE2]/85" : "bg-transparent"}`} />
      ))}
    </div>
  );
}
